import React, { useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { 
  Phone as PhoneIcon, 
  Lock as LockIcon, 
  Visibility, 
  VisibilityOff,
  CheckCircle as CheckIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { initiatePasswordReset, verifyResetCode, resetPassword } from '../adminsApi';
import { useTranslation } from 'react-i18next';

const ResetPassword = () => {
  const { t } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [formData, setFormData] = useState({
    phoneNumber: '',
    oldPassword: '',
    verificationCode: '',
    newPassword: '',
    confirmPassword: ''
  });

  const steps = [
    t('VerifyIdentity'),
    t('EnterCode'),
    t('NewPassword')
  ];

  const passwordRequirements = [
    t('AtLeast8Characters'),
    t('ContainsUppercase'),
    t('ContainsLowercase'),
    t('ContainsNumber'),
    t('ContainsSpecialChar')
  ];

  const stripPhonePrefix = (number) => {
    if (!number) return '';
    return number.toString().replace(/^\+967/, '');
  };

  const addPhonePrefix = (number) => {
    if (!number) return '';
    const cleaned = stripPhonePrefix(number);
    return cleaned ? `+967${cleaned}` : '';
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Step 1: Verify old password and send code
  const handleStep1Submit = async (e) => {
    e.preventDefault();
    if (!formData.phoneNumber || !formData.oldPassword) {
      Swal.fire(t('Error'), t('fillAllRequiredFields'), 'error');
      return;
    }

    setLoading(true);
    try {
      const phoneWithPrefix = addPhonePrefix(formData.phoneNumber);
      await initiatePasswordReset(phoneWithPrefix, formData.oldPassword);
      Swal.fire(t('Success'), t('CodeSentSuccessfully'), 'success');
      setActiveStep(1);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t('FailedToSendCode');
      Swal.fire(t('Error'), errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify code
  const handleStep2Submit = async (e) => {
    e.preventDefault();
    if (!formData.verificationCode || formData.verificationCode.length !== 6) {
      Swal.fire(t('Error'), t('Enter6DigitCode'), 'error');
      return;
    }

    setLoading(true);
    try {
      const phoneWithPrefix = addPhonePrefix(formData.phoneNumber);
      await verifyResetCode(phoneWithPrefix, formData.verificationCode);
      Swal.fire(t('Success'), t('CodeVerifiedSuccessfully'), 'success');
      setActiveStep(2);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t('InvalidVerificationCode');
      Swal.fire(t('Error'), errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Step 3: Reset password
  const handleStep3Submit = async (e) => {
    e.preventDefault();
    if (!formData.newPassword || !formData.confirmPassword) {
      Swal.fire(t('Error'), t('fillAllRequiredFields'), 'error');
      return;
    }

    if (formData.newPassword !== formData.confirmPassword) {
      Swal.fire(t('Error'), t('PasswordsDoNotMatch'), 'error');
      return;
    }

    setLoading(true);
    try {
      const phoneWithPrefix = addPhonePrefix(formData.phoneNumber);
      await resetPassword(phoneWithPrefix, formData.newPassword, formData.confirmPassword);
      
      await Swal.fire({
        icon: 'success',
        title: t('PasswordResetSuccessful'),
        text: t('CanNowLoginWithNewPassword'),
        confirmButtonText: 'OK'
      });

      // Reset form
      setFormData({
        phoneNumber: '',
        oldPassword: '',
        verificationCode: '',
        newPassword: '',
        confirmPassword: ''
      });
      setActiveStep(0);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || t('FailedToResetPassword');
      const errors = error.response?.data?.errors;
      
      let errorHtml = errorMessage;
      if (errors && errors.length > 0) {
        errorHtml += '<ul style="text-align: left; margin-top: 10px;">';
        errors.forEach(err => {
          errorHtml += `<li>${err}</li>`;
        });
        errorHtml += '</ul>';
      }
      
      Swal.fire({
        icon: 'error',
        title: t('Error'),
        html: errorHtml
      });
    } finally {
      setLoading(false);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <form onSubmit={handleStep1Submit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('phoneNumberLabel')}
                name="phoneNumber"
                placeholder="7XXXXXXXX"
                value={formData.phoneNumber}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 9);
                  handleChange({
                    target: { name: 'phoneNumber', value: digits }
                  });
                }}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneIcon sx={{ color: 'action.active', mr: 0.5 }} />
                      <Typography>+967</Typography>
                    </InputAdornment>
                  ),
                }}
                inputProps={{ maxLength: 9 }}
                helperText={t('EnterYourRegisteredPhoneNumber')}
              />

              <TextField
                fullWidth
                label={t('OldPassword')}
                name="oldPassword"
                type={showOldPassword ? 'text' : 'password'}
                placeholder={t('EnterOldPassword')}
                value={formData.oldPassword}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowOldPassword(!showOldPassword)}
                        edge="end"
                      >
                        {showOldPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading && <CircularProgress size={20} />}
              >
                {loading ? t('SendingCode') : t('SendVerificationCode')}
              </Button>
            </Box>
          </form>
        );

      case 1:
        return (
          <form onSubmit={handleStep2Submit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Alert severity="info">
                {t('CodeSentToWhatsApp')} +967{formData.phoneNumber}
              </Alert>

              <TextField
                fullWidth
                label={t('VerificationCode')}
                name="verificationCode"
                placeholder={t('Enter6DigitCode')}
                value={formData.verificationCode}
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '').slice(0, 6);
                  handleChange({
                    target: { name: 'verificationCode', value: digits }
                  });
                }}
                required
                inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '24px', letterSpacing: '8px' } }}
                helperText={t('EnterCodeSentToWhatsApp')}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(0)}
                  disabled={loading}
                >
                  {t('Back')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('Verifying') : t('VerifyCode')}
                </Button>
              </Box>
            </Box>
          </form>
        );

      case 2:
        return (
          <form onSubmit={handleStep3Submit}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <Alert severity="success" icon={<CheckIcon />}>
                {t('CodeVerifiedCreateNewPassword')}
              </Alert>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('PasswordRequirements')}
                </Typography>
                <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                  {passwordRequirements.map((req, idx) => (
                    <Typography key={idx} component="li" variant="body2" color="text.secondary">
                      {req}
                    </Typography>
                  ))}
                </Box>
              </Box>

              <TextField
                fullWidth
                label={t('NewPassword')}
                name="newPassword"
                type={showNewPassword ? 'text' : 'password'}
                placeholder={t('EnterNewPassword')}
                value={formData.newPassword}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                label={t('ConfirmNewPassword')}
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('ReEnterNewPassword')}
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: 'action.active' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                error={formData.confirmPassword && formData.newPassword !== formData.confirmPassword}
                helperText={
                  formData.confirmPassword && formData.newPassword !== formData.confirmPassword
                    ? t('PasswordsDoNotMatch')
                    : ''
                }
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={() => setActiveStep(1)}
                  disabled={loading}
                >
                  {t('Back')}
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={loading}
                  startIcon={loading && <CircularProgress size={20} />}
                >
                  {loading ? t('ResettingPassword') : t('ResetPasswordButton')}
                </Button>
              </Box>
            </Box>
          </form>
        );

      default:
        return null;
    }
  };

  return (
    <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <Card sx={{ maxWidth: 600, width: '100%' }}>
        <CardHeader
          title={t('ResetAdminPassword')}
          sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}
        />
        <CardContent sx={{ p: 4 }}>
          <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {renderStepContent()}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ResetPassword;